import type { ColumnCatalogDef } from '@/types/column-catalog'
import { createColumnDef as col } from '@/components/grid/column-builder'
import { MAX_WATCHLIST_FILTER_LISTS } from '@/features/watchlist/schemas/watchlist-types'
import {
  COUNTRY_ISO_OPTIONS,
  COUNTRY_OPTIONS,
  SHARE_GROUP_OPTIONS,
  EXCHANGE_OPTIONS,
  INDUSTRY_OPTIONS,
  SECTOR_OPTIONS,
  DILUTION_OPTIONS,
  YES_NO_INTEGER_OPTIONS,
} from '@/column-registry/enum-options'

export type ScannerColumnDef = ColumnCatalogDef

// ─── Commonly Used ───

const COMMONLY_USED: ScannerColumnDef[] = [
  col('session_chg', 'Chg $', 'currency', 'Commonly Used', {
    formLabel: 'Change $',
    timeSliceable: true,
  }),
  col('session_chg_p', 'Chg %', 'percent', 'Commonly Used', {
    formLabel: 'Change %',
    timeSliceable: true,
  }),
  col('session_lp', 'Price', 'currency', 'Commonly Used', { formLabel: 'Last Price' }),
  col('session_v', 'Vol', 'volume', 'Commonly Used', {
    formLabel: 'Volume',
    timeSliceable: true,
    keywords: ['volume', 'day volume', 'session volume'],
  }),
  col('rvol', 'RVOL', 'number', 'Commonly Used', {
    keywords: ['relative volume', 'rel vol'],
  }),
  // Frontend-computed RVOL vs the user's custom reference days (Advanced tab).
  // `displayOnly` keeps it out of the filter builder (no such backend field);
  // it's stripped from the wire request via FRONTEND_ONLY_SCANNER_COLUMNS and
  // the row value is enriched client-side in ScannerDatasource.fetchRows.
  {
    ...col('custom_rvol', 'RVOL×', 'number', 'Commonly Used', {
      keywords: ['custom rvol', 'relative volume', 'reference days', 'rvol vs'],
    }),
    displayOnly: true,
  },
  col('dollar_vol', 'Dollar Vol', 'volume', 'Commonly Used', {
    columnType: 'largePrice',
    keywords: ['dolvol', 'dv', 'dollar volume', '$vol'],
  }),
  col('trades', 'Trades', 'number', 'Commonly Used', {
    columnType: 'tradeCount',
    keywords: ['trade count', 'ticks'],
  }),
  col('pm_chg', 'PM Gap', 'currency', 'Commonly Used', {
    isPremarket: true,
    formLabel: 'PM Gap',
    keywords: ['premarket gap', 'gap'],
  }),
  col('pm_chg_p', 'PM Gap %', 'percent', 'Commonly Used', {
    isPremarket: true,
    formLabel: 'PM Gap %',
    keywords: ['premarket gap percent', 'gap up', 'gap %'],
  }),
  col('pm_v', 'PM Vol', 'volume', 'Commonly Used', {
    isPremarket: true,
    keywords: ['premarket volume', 'pm volume'],
  }),
]

// ─── Day 1 Intraday ───

const DAY0_INTRADAY: ScannerColumnDef[] = [
  // Live `halt` is an INTEGER 0/1 flag — modelled as a Yes/No multi-select
  // (legacy parity). User picks the label, filter receives '1' / '0'.
  col('halt', 'Halt', 'enum', 'Day 1 Intraday', { enumOptions: YES_NO_INTEGER_OPTIONS }),
  col('range', 'Range', 'currency', 'Day 1 Intraday'),
  col('range_pct', 'Pos Rng %', 'percent', 'Day 1 Intraday', { formLabel: 'Position in Range %' }),
  col('o_chg', 'Open Chg', 'currency', 'Day 1 Intraday', { formLabel: 'Change from Open' }),
  col('o_chg_p', 'Open Chg %', 'percent', 'Day 1 Intraday', { formLabel: 'Change from Open %' }),
  col('lp_5_chg_p', '5 Min Chg %', 'percent', 'Day 1 Intraday'),
  col('lp_10_chg_p', '10 Min Chg %', 'percent', 'Day 1 Intraday'),
  col('lp_30_chg_p', '30 Min Chg %', 'percent', 'Day 1 Intraday'),
  col('chg_h_o_pct', 'Open High %', 'percent', 'Day 1 Intraday', { formLabel: 'High from Open %' }),
  col('chg_h_pc_pct', 'High %', 'percent', 'Day 1 Intraday', {
    formLabel: 'High from Prev Close %',
  }),
  col('chg_l_o_pct', 'Open Low %', 'percent', 'Day 1 Intraday', { formLabel: 'Low from Open %' }),
  col('chg_l_pc_pct', 'Low %', 'percent', 'Day 1 Intraday', { formLabel: 'Low from Prev Close %' }),
  col('o', 'Open', 'currency', 'Day 1 Intraday'),
  col('h', 'High', 'currency', 'Day 1 Intraday'),
  col('l', 'Low', 'currency', 'Day 1 Intraday'),
  col('c', 'Close', 'currency', 'Day 1 Intraday'),
  // SSR is an INTEGER 0/1 flag — modelled as a Yes/No multi-select (legacy
  // parity). User picks the label, filter receives '1' / '0'.
  col('ssr', 'SSR', 'enum', 'Day 1 Intraday', { enumOptions: YES_NO_INTEGER_OPTIONS }),
  col('vw', 'VWAP', 'currency', 'Day 1 Intraday'),
  col('rth_pullback_pct', 'RTH Pullback %', 'percent', 'Day 1 Intraday'),
  col('chg_allsession_h_pc_pct', 'All Sess High %', 'percent', 'Day 1 Intraday'),
  col('chg_allsession_l_pc_pct', 'All Sess Low %', 'percent', 'Day 1 Intraday'),
  col('chg_allsession_range_pct', 'Pos Rng All Sess %', 'percent', 'Day 1 Intraday'),
  col('chg_allday_h_pc_pct', 'All Day High %', 'percent', 'Day 1 Intraday'),
  col('chg_allday_l_pc_pct', 'All Day Low %', 'percent', 'Day 1 Intraday'),
  col('chg_allday_range_pct', 'Pos Rng All Day %', 'percent', 'Day 1 Intraday'),
]

// ─── Day 1 Premarket ───

const DAY0_PREMARKET: ScannerColumnDef[] = [
  col('pm_lp', 'PM Price', 'currency', 'Day 1 Premarket', { isPremarket: true }),
  col('pm_h', 'PM High', 'currency', 'Day 1 Premarket', { isPremarket: true }),
  col('pm_l', 'PM Low', 'currency', 'Day 1 Premarket', { isPremarket: true }),
  col('pm_range', 'PM Rng', 'currency', 'Day 1 Premarket', { isPremarket: true }),
  col('pm_range_pct', 'Pos PM Rng %', 'percent', 'Day 1 Premarket', {
    isPremarket: true,
    formLabel: 'PM Position in Range %',
  }),
  col('chg_pm_h_pc_pct', 'PM High %', 'percent', 'Day 1 Premarket', {
    isPremarket: true,
    formLabel: 'PM High from Prev Close %',
  }),
  col('chg_pm_l_pc_pct', 'PM Low %', 'percent', 'Day 1 Premarket', {
    isPremarket: true,
    formLabel: 'PM Low from Prev Close %',
  }),
  col('pm_pullback_pct', 'PM Pullback %', 'percent', 'Day 1 Premarket', { isPremarket: true }),
  col('ext_hours_v', 'Ext Hours Vol', 'volume', 'Day 1 Premarket'),
]

// ─── Day 1 Afterhours ───

const DAY0_AFTERHOURS: ScannerColumnDef[] = [
  col('ah_chg', 'AH Chg', 'currency', 'Day 1 Afterhours', {
    isAfterhours: true,
    formLabel: 'AH Change',
  }),
  col('ah_chg_p', 'AH Chg %', 'percent', 'Day 1 Afterhours', {
    isAfterhours: true,
    formLabel: 'AH Change %',
  }),
  col('ah_lp', 'AH Price', 'currency', 'Day 1 Afterhours', { isAfterhours: true }),
  col('ah_h', 'AH High', 'currency', 'Day 1 Afterhours', { isAfterhours: true }),
  col('ah_l', 'AH Low', 'currency', 'Day 1 Afterhours', { isAfterhours: true }),
  col('ah_v', 'AH Vol', 'volume', 'Day 1 Afterhours', { isAfterhours: true }),
  col('ah_range', 'AH Rng', 'currency', 'Day 1 Afterhours', { isAfterhours: true }),
  col('ah_range_pct', 'Pos AH Rng %', 'percent', 'Day 1 Afterhours', {
    isAfterhours: true,
    formLabel: 'AH Position in Range %',
  }),
  col('chg_ah_h_pc_pct', 'AH High %', 'percent', 'Day 1 Afterhours', {
    isAfterhours: true,
    formLabel: 'AH High from Close %',
  }),
  col('chg_ah_l_pc_pct', 'AH Low %', 'percent', 'Day 1 Afterhours', {
    isAfterhours: true,
    formLabel: 'AH Low from Close %',
  }),
  col('ah_pullback_pct', 'AH Pullback %', 'percent', 'Day 1 Afterhours', { isAfterhours: true }),
]

// ─── Ranges ───

function generateRanges(): ScannerColumnDef[] {
  const cat = 'Ranges'
  const result: ScannerColumnDef[] = []

  // Multi-day change
  for (const n of [2, 3, 4, 5]) {
    result.push(col(`pc_${n}_chg_p`, `${n} Day Chg %`, 'percent', cat, { isHistorical: true }))
    result.push(col(`pc_${n}_chg`, `${n} Day Chg`, 'currency', cat, { isHistorical: true }))
  }

  // Extended multi-day changes (10, 21, 63, 126, 252, YTD)
  for (const n of [10, 21, 63, 126, 252]) {
    const label =
      n === 10 ? '10 Day' : n === 21 ? '1 Mo' : n === 63 ? '3 Mo' : n === 126 ? '6 Mo' : '1 Yr'
    result.push(col(`pc_${n}_chg_p`, `${label} Chg %`, 'percent', cat, { isHistorical: true }))
    result.push(col(`pc_${n}_chg`, `${label} Chg`, 'currency', cat, { isHistorical: true }))
  }
  result.push(col('pc_ytd_chg_p', 'YTD Chg %', 'percent', cat, { isHistorical: true }))
  result.push(col('pc_ytd_chg', 'YTD Chg', 'currency', cat, { isHistorical: true }))

  // Multi-day high/low % (2..5, 10 day)
  for (const n of [2, 3, 4, 5, 10]) {
    result.push(col(`day_${n}_high_pct`, `${n} Day High %`, 'percent', cat, { isHistorical: true }))
  }
  for (const n of [2, 3, 4, 5, 10]) {
    result.push(col(`day_${n}_low_pct`, `${n} Day Low %`, 'percent', cat, { isHistorical: true }))
  }

  // Position in range %
  for (const n of [1, 2, 3, 4, 5, 10, 21, 63, 126, 252]) {
    const label =
      n <= 10 ? `${n}` : n === 21 ? '1 Mo' : n === 63 ? '3 Mo' : n === 126 ? '6 Mo' : '1 Yr'
    result.push(
      col(`day_${n}_range_pct`, `Pos ${label} Day Rng %`, 'percent', cat, { isHistorical: n > 1 }),
    )
  }
  result.push(col('day_ytd_range_pct', 'Pos YTD Rng %', 'percent', cat, { isHistorical: true }))

  // High/Low % from N-day range
  for (const n of [21, 63, 126, 252]) {
    const label = n === 21 ? '1 Mo' : n === 63 ? '3 Mo' : n === 126 ? '6 Mo' : '1 Yr'
    result.push(col(`day_${n}_high_pct`, `${label} High %`, 'percent', cat, { isHistorical: true }))
  }
  for (const n of [21, 63, 126, 252]) {
    const label = n === 21 ? '1 Mo' : n === 63 ? '3 Mo' : n === 126 ? '6 Mo' : '1 Yr'
    result.push(col(`day_${n}_low_pct`, `${label} Low %`, 'percent', cat, { isHistorical: true }))
  }
  result.push(col('day_ytd_high_pct', 'YTD High %', 'percent', cat, { isHistorical: true }))
  result.push(col('day_ytd_low_pct', 'YTD Low %', 'percent', cat, { isHistorical: true }))

  // N-day high/low prices
  for (const n of [2, 3, 4, 5, 10, 21, 63, 126, 252]) {
    const label =
      n <= 10 ? `${n} Day` : n === 21 ? '1 Mo' : n === 63 ? '3 Mo' : n === 126 ? '6 Mo' : '1 Year'
    result.push(col(`day_${n}_high`, `${label} High`, 'currency', cat, { isHistorical: true }))
  }
  result.push(col('day_ytd_high', 'YTD High', 'currency', cat, { isHistorical: true }))

  for (const n of [2, 3, 4, 5, 10, 21, 63, 126, 252]) {
    const label =
      n <= 10 ? `${n} Day` : n === 21 ? '1 Mo' : n === 63 ? '3 Mo' : n === 126 ? '6 Mo' : '1 Year'
    result.push(col(`day_${n}_low`, `${label} Low`, 'currency', cat, { isHistorical: true }))
  }
  result.push(col('day_ytd_low', 'YTD Low', 'currency', cat, { isHistorical: true }))

  return result
}

// ─── Indicators ───

function generateIndicators(): ScannerColumnDef[] {
  const cat = 'Indicators'
  const result: ScannerColumnDef[] = []

  // ATR
  for (const n of [7, 14, 30, 60]) {
    result.push(
      col(`day_m1_atr_${n}`, `ATR${n}`, 'currency', cat, { group: 'ATR', isHistorical: true }),
    )
  }
  for (const n of [1, 2, 3]) {
    result.push(
      col(`day_m${n}_atr_14_pct`, `D[-${n}] ATR14 %`, 'percent', cat, {
        group: 'ATR',
        isHistorical: true,
      }),
    )
  }
  // Extra ATR for deeper history
  for (const n of [2, 3, 4]) {
    result.push(
      col(`day_m${n}_atr_14`, `D[-${n}] ATR14`, 'currency', cat, {
        group: 'ATR',
        isHistorical: true,
      }),
    )
  }

  // ADTV
  for (const n of [7, 14, 30, 60]) {
    result.push(
      col(`day_m1_adtv_${n}`, `ADTV${n}`, 'volume', cat, { group: 'ADTV', isHistorical: true }),
    )
  }

  // Consecutive days
  result.push(
    col('day_m1_consec_green', 'Consec. Green', 'number', cat, {
      group: 'Consec. Days',
      isHistorical: true,
    }),
  )
  result.push(
    col('day_m1_consec_red', 'Consec. Red', 'number', cat, {
      group: 'Consec. Days',
      isHistorical: true,
    }),
  )
  result.push(
    col('day_m1_consec_green_bar', 'Consec. Green Bar', 'number', cat, {
      group: 'Consec. Days',
      isHistorical: true,
    }),
  )
  result.push(
    col('day_m1_consec_red_bar', 'Consec. Red Bar', 'number', cat, {
      group: 'Consec. Days',
      isHistorical: true,
    }),
  )
  result.push(
    col('day_m1_consec_gap_up', 'Consec. Gap Up', 'number', cat, {
      group: 'Consec. Days',
      isHistorical: true,
    }),
  )
  result.push(
    col('day_m1_consec_gap_down', 'Consec. Gap Down', 'number', cat, {
      group: 'Consec. Days',
      isHistorical: true,
    }),
  )

  // Pivot Points
  result.push(
    col('day_m1_pp_pivot', 'Pivot Point', 'currency', cat, {
      group: 'Pivot Points',
      isHistorical: true,
    }),
  )
  for (const level of ['r1', 'r2', 'r3', 's1', 's2', 's3']) {
    const label = `Pivot ${level.toUpperCase()}`
    result.push(
      col(`day_m1_pp_${level}`, label, 'currency', cat, {
        group: 'Pivot Points',
        isHistorical: true,
      }),
    )
  }
  result.push(
    col('pp_pivot_pct', 'Dist Pivot %', 'percent', cat, {
      group: 'Pivot Points',
      isHistorical: true,
    }),
  )
  for (const level of ['r1', 'r2', 'r3', 's1', 's2', 's3']) {
    result.push(
      col(`pp_${level}_pct`, `Dist Pivot ${level.toUpperCase()} %`, 'percent', cat, {
        group: 'Pivot Points',
        isHistorical: true,
      }),
    )
  }
  result.push(
    col('day_m2_pp_pivot', 'D[-1] Pivot Point', 'currency', cat, {
      group: 'Pivot Points',
      isHistorical: true,
    }),
  )
  result.push(
    col('day_m3_pp_pivot', 'D[-2] Pivot Point', 'currency', cat, {
      group: 'Pivot Points',
      isHistorical: true,
    }),
  )

  // EMA
  for (const n of [9, 21, 50, 72, 89, 100, 200]) {
    result.push(
      col(`day_m1_ema_${n}`, `D[-1] EMA${n}`, 'currency', cat, {
        group: 'EMA',
        isHistorical: true,
      }),
    )
  }
  for (const n of [9, 21, 50, 72, 89, 100, 200]) {
    result.push(
      col(`ema_${n}_pct`, `EMA${n} Chg %`, 'percent', cat, { group: 'EMA', isHistorical: true }),
    )
  }

  // SMA
  for (const n of [9, 21, 50, 72, 89, 100, 200]) {
    result.push(
      col(`day_m1_sma_${n}`, `D[-1] SMA${n}`, 'currency', cat, {
        group: 'SMA',
        isHistorical: true,
      }),
    )
  }
  for (const n of [9, 21, 50, 72, 89, 100, 200]) {
    result.push(
      col(`sma_${n}_pct`, `SMA${n} Chg %`, 'percent', cat, { group: 'SMA', isHistorical: true }),
    )
  }

  return result
}

// ─── Previous Days ───

function generatePrevDays(): ScannerColumnDef[] {
  const cat = 'Previous Days'
  const result: ScannerColumnDef[] = []

  // D[-1..5] OHLCV
  for (const n of [1, 2, 3, 4, 5]) {
    const d = `D[-${n}]`
    const prefix = `day_m${n}`
    result.push(col(`${prefix}_open`, `${d} Open`, 'currency', cat, { isHistorical: true }))
    result.push(col(`${prefix}_high`, `${d} High`, 'currency', cat, { isHistorical: true }))
    result.push(col(`${prefix}_low`, `${d} Low`, 'currency', cat, { isHistorical: true }))
    result.push(col(`${prefix}_close`, `${d} Close`, 'currency', cat, { isHistorical: true }))
    result.push(col(`${prefix}_vol`, `${d} Vol`, 'volume', cat, { isHistorical: true }))
    result.push(col(`${prefix}_vw`, `${d} Vwap`, 'currency', cat, { isHistorical: true }))
    result.push(col(`${prefix}_range`, `${d} Range`, 'currency', cat, { isHistorical: true }))
    result.push(
      col(`${prefix}_chg`, `${d} Chg $`, 'currency', cat, {
        isHistorical: true,
        formLabel: `${d} Change $`,
      }),
    )
    result.push(col(`${prefix}_chg_pct`, `${d} Chg %`, 'percent', cat, { isHistorical: true }))
    result.push(col(`${prefix}_gap`, `${d} Gap`, 'currency', cat, { isHistorical: true }))
    result.push(col(`${prefix}_gap_pct`, `${d} Gap %`, 'percent', cat, { isHistorical: true }))
    // "Change from Open" family — old app grid labels (see history-column-defs).
    result.push(
      col(`${prefix}_ret_open`, `${d} Chg $ From Open`, 'currency', cat, {
        isHistorical: true,
        formLabel: `${d} Change $ From Open`,
      }),
    )
    result.push(
      col(`${prefix}_ret_open_pct`, `${d} Chg % From Open`, 'percent', cat, {
        isHistorical: true,
        formLabel: `${d} Change % From Open`,
      }),
    )
  }

  // D[-1] extra columns
  result.push(
    col('day_m1_trades', 'D[-1] Trades', 'number', cat, {
      columnType: 'tradeCount',
      isHistorical: true,
    }),
  )
  result.push(
    col('day_m1_range_close_pct', 'D[-1] Pos Rng %', 'percent', cat, {
      isHistorical: true,
      formLabel: 'D[-1] Position in Range %',
    }),
  )
  result.push(col('day_m1_close_high_pct', 'D[-1] High %', 'percent', cat, { isHistorical: true }))
  result.push(col('day_m1_close_low_pct', 'D[-1] Low %', 'percent', cat, { isHistorical: true }))
  result.push(
    col('day_m1_open_high_pct', 'D[-1] High % From Open', 'percent', cat, {
      isHistorical: true,
      formLabel: 'D[-1] Open High %',
    }),
  )
  result.push(
    col('day_m1_open_low_pct', 'D[-1] Low % From Open', 'percent', cat, {
      isHistorical: true,
      formLabel: 'D[-1] Open Low %',
    }),
  )
  // Yes/No multi-select (legacy parity) — same shape as the live `ssr`.
  result.push(
    col('day_m1_close_ssr', 'D[-1] SSR', 'enum', cat, {
      isHistorical: true,
      enumOptions: YES_NO_INTEGER_OPTIONS,
    }),
  )
  result.push(col('day_m1_rvol_5', 'D[-1] RVOL 5', 'number', cat, { isHistorical: true }))
  result.push(
    col('day_m1_dollar_vol', 'D[-1] Dollar Vol', 'volume', cat, {
      columnType: 'largePrice',
      isHistorical: true,
    }),
  )

  // D[-1] PM columns
  result.push(
    col('day_m1_pm_low', 'D[-1] PM Low', 'currency', cat, {
      isHistorical: true,
      isPremarket: true,
    }),
  )
  result.push(
    col('day_m1_pm_high', 'D[-1] PM High', 'currency', cat, {
      isHistorical: true,
      isPremarket: true,
    }),
  )
  result.push(
    col('day_m1_pm_vol', 'D[-1] PM Vol', 'volume', cat, { isHistorical: true, isPremarket: true }),
  )
  result.push(
    col('day_m1_pm_range', 'D[-1] PM Rng', 'currency', cat, {
      isHistorical: true,
      isPremarket: true,
    }),
  )
  result.push(
    col('day_m1_pm_low_pct', 'D[-1] PM Low %', 'percent', cat, {
      isHistorical: true,
      isPremarket: true,
    }),
  )
  result.push(
    col('day_m1_pm_high_pct', 'D[-1] PM High %', 'percent', cat, {
      isHistorical: true,
      isPremarket: true,
    }),
  )
  result.push(
    col('day_m1_pm_open_range_pct', 'D[-1] Pos PM Rng %', 'percent', cat, {
      isHistorical: true,
      isPremarket: true,
    }),
  )

  // D[-1] AH columns
  result.push(
    col('day_m1_ah_high', 'D[-1] AH High', 'currency', cat, {
      isHistorical: true,
      isAfterhours: true,
    }),
  )
  result.push(
    col('day_m1_ah_low', 'D[-1] AH Low', 'currency', cat, {
      isHistorical: true,
      isAfterhours: true,
    }),
  )
  result.push(
    col('day_m1_ah_close', 'D[-1] AH Close', 'currency', cat, {
      isHistorical: true,
      isAfterhours: true,
    }),
  )
  result.push(
    col('day_m1_ah_vol', 'D[-1] AH Vol', 'volume', cat, { isHistorical: true, isAfterhours: true }),
  )
  result.push(
    col('day_m1_ah_chg', 'D[-1] AH Chg', 'currency', cat, {
      isHistorical: true,
      isAfterhours: true,
    }),
  )
  result.push(
    col('day_m1_ah_chg_pct', 'D[-1] AH Chg %', 'percent', cat, {
      isHistorical: true,
      isAfterhours: true,
    }),
  )
  result.push(
    col('day_m1_ah_range', 'D[-1] AH Rng', 'currency', cat, {
      isHistorical: true,
      isAfterhours: true,
    }),
  )
  result.push(
    col('day_m1_ah_range_pct', 'D[-1] Pos AH Rng %', 'percent', cat, {
      isHistorical: true,
      isAfterhours: true,
    }),
  )
  result.push(
    col('day_m1_ah_low_pct', 'D[-1] AH Low %', 'percent', cat, {
      isHistorical: true,
      isAfterhours: true,
    }),
  )
  result.push(
    col('day_m1_ah_high_pct', 'D[-1] AH High %', 'percent', cat, {
      isHistorical: true,
      isAfterhours: true,
    }),
  )

  // D[-1] hourly high/low
  const hourlyWindows = [
    '0930_1000',
    '1000_1100',
    '1100_1200',
    '1200_1300',
    '1300_1400',
    '1400_1500',
    '1500_1600',
  ]
  for (const w of hourlyWindows) {
    const [s, e] = [w.slice(0, 4), w.slice(5)]
    const timeLabel = `${s.slice(0, 2)}:${s.slice(2)}-${e.slice(0, 2)}:${e.slice(2)}`
    result.push(
      col(`day_m1_h_${w}_high`, `D[-1] ${timeLabel} High`, 'currency', cat, { isHistorical: true }),
    )
    result.push(
      col(`day_m1_h_${w}_low`, `D[-1] ${timeLabel} Low`, 'currency', cat, { isHistorical: true }),
    )
  }

  return result
}

// ─── Dilution ───

// All seven dilution-risk columns share `DILUTION_OPTIONS` and form a single
// comparable family. Tagging each with `columnType: 'enum_dilution'` makes
// the family pairwise compatible while keeping them isolated from unrelated
// enums like exchange / share_group / halt.
const DILUTION: ScannerColumnDef[] = [
  col('offering_ability', 'Offering Ability', 'enum', 'Dilution', {
    columnType: 'enum_dilution',
    enumOptions: DILUTION_OPTIONS,
  }),
  col('dilution', 'Dilution', 'enum', 'Dilution', {
    columnType: 'enum_dilution',
    enumOptions: DILUTION_OPTIONS,
  }),
  col('offering_frequency', 'Offering Frequency', 'enum', 'Dilution', {
    columnType: 'enum_dilution',
    enumOptions: DILUTION_OPTIONS,
  }),
  col('cash_need', 'Cash Need', 'enum', 'Dilution', {
    columnType: 'enum_dilution',
    enumOptions: DILUTION_OPTIONS,
  }),
  col('nasdaq_compliance', 'NASDAQ Compliance', 'enum', 'Dilution', {
    columnType: 'enum_dilution',
    enumOptions: DILUTION_OPTIONS,
  }),
  col('overall_offering_risk', 'Overall Offering Risk', 'enum', 'Dilution', {
    columnType: 'enum_dilution',
    enumOptions: DILUTION_OPTIONS,
  }),
  col('warrant_exercise', 'Warrant Exercise', 'enum', 'Dilution', {
    columnType: 'enum_dilution',
    enumOptions: DILUTION_OPTIONS,
  }),
]

// ─── Financials ───

const FINANCIALS: ScannerColumnDef[] = [
  // Balance-sheet & income-statement dollar amounts: legacy `largePrice`,
  // distinct from share-volume flows.
  col('cashandcashequivalents', 'Cash & Equiv', 'volume', 'Financials', {
    columnType: 'largePrice',
  }),
  col('cashcashequivalentsandshortterminvestments', 'Short Term Cash', 'volume', 'Financials', {
    columnType: 'largePrice',
  }),
  col('currentassets', 'Current Assets', 'volume', 'Financials', { columnType: 'largePrice' }),
  col('currentliabilities', 'Current Liabilities', 'volume', 'Financials', {
    columnType: 'largePrice',
  }),
  col('netincome', 'Net Income', 'volume', 'Financials', { columnType: 'largePrice' }),
  col('revenue', 'Revenue', 'volume', 'Financials', { columnType: 'largePrice' }),
  // Ratios / counts: arbitraryNumber by default — they don't pair with each
  // other or with magnitudes by default.
  col('currentratio', 'Current Ratio', 'number', 'Financials'),
  col('peratio', 'P/E Ratio', 'number', 'Financials'),
  col('earnings_diff', 'Earnings Date Diff', 'number', 'Financials'),
  col('earnings_date', 'Earnings Date', 'date', 'Financials'),
  col('split_diff', 'Split Date Diff', 'number', 'Financials'),
  col('split_date', 'Split Date', 'date', 'Financials'),
  col('split_ratio', 'Split Ratio', 'number', 'Financials'),
  // EPS-family (dollar per share) and percent-based growth/margin metrics.
  col('dilutedeps', 'Diluted EPS', 'currency', 'Financials'),
  col('eps', 'EPS', 'currency', 'Financials'),
  col('eps_growth_q', 'EPS QoQ Growth %', 'percent', 'Financials'),
  // grossmargin / netmargin are *percentages* mis-typed as `volume` in the
  // current schema. Pin them to `percentage` so they pair only with each
  // other and other margin/percent metrics.
  col('grossmargin', 'Gross Margin', 'volume', 'Financials', { columnType: 'percentage' }),
  col('netmargin', 'Net Margin', 'volume', 'Financials', { columnType: 'percentage' }),
  col('revenue_growth_q', 'Revenue QoQ %', 'percent', 'Financials'),
  col('roa', 'ROA', 'percent', 'Financials'),
  col('roe', 'ROE', 'percent', 'Financials'),
]

// ─── Share Structure ───

const SHARE_STRUCTURE: ScannerColumnDef[] = [
  col('marketcap', 'Market Cap', 'volume', 'Share Structure', {
    keywords: ['mcap', 'mc', 'cap'],
  }),
  col('sharesoutstanding', 'Shares Out', 'volume', 'Share Structure', {
    keywords: ['so', 'shares outstanding', 'total shares'],
  }),
  col('sharesfloat', 'Float', 'volume', 'Share Structure', {
    keywords: ['public float', 'floating shares', 'shares float'],
  }),
  col('percentinsiders', 'Insider Own %', 'percent', 'Share Structure', {
    keywords: ['insider ownership', 'insiders'],
  }),
  col('percentinstitutions', 'Inst. Own %', 'percent', 'Share Structure', {
    keywords: ['institutional ownership', 'institutions'],
  }),
  col('shortpercentfloat', 'Short Float', 'percent', 'Share Structure', {
    keywords: ['short interest', 'si', 'squeeze'],
  }),
  col('sharesshort', 'Shares Short', 'volume', 'Share Structure', {
    keywords: ['short interest shares', 'short shares'],
  }),
  col('floatrotation', 'Float Rotation', 'number', 'Share Structure', {
    keywords: ['turnover', 'rotation'],
  }),
]

// ─── Company ───

const COMPANY: ScannerColumnDef[] = [
  col('ticker', 'Ticker', 'string', 'Company'),
  col('name', 'Company Name', 'string', 'Company'),
  col('country_iso', 'Country Code', 'enum', 'Company', { enumOptions: COUNTRY_ISO_OPTIONS }),
  col('country', 'Country', 'enum', 'Company', { enumOptions: COUNTRY_OPTIONS }),
  col('share_group', 'Share Group', 'enum', 'Company', { enumOptions: SHARE_GROUP_OPTIONS }),
  col('exchange', 'Exchange', 'enum', 'Company', { enumOptions: EXCHANGE_OPTIONS }),
  col('industry', 'Industry', 'enum', 'Company', { enumOptions: INDUSTRY_OPTIONS }),
  col('sector', 'Sector', 'enum', 'Company', { enumOptions: SECTOR_OPTIONS }),
  col('ipo_date', 'IPO Date', 'date', 'Company'),
]

// ─── General ───

const GENERAL: ScannerColumnDef[] = [
  // pr_count and non_pr_count are pairwise comparable to each other (legacy
  // explicit opt-in) but not to anything else by default.
  col('pr_count', 'PR', 'number', 'General', {
    comparesTo: ['name.non_pr_count'],
  }),
  col('non_pr_count', 'Non-PR', 'number', 'General', {
    comparesTo: ['name.pr_count'],
  }),
  // Total news is a misc-count — incomparable to other counts/magnitudes.
  // Falls through to the default `arbitraryNumber` from `dataType: 'number'`.
  col('total_news_count', 'News', 'number', 'General'),
  // Recency diffs in business days, earnings_diff encoding (0.25 = today
  // premarket, -0.25 = yesterday after hours, null = none in 30 days).
  col('pr_diff', 'PR Date Diff', 'number', 'General'),
  col('filing_diff', 'Filing Date Diff', 'number', 'General'),
  col('event_diff', 'PR/Filing Date Diff', 'number', 'General'),
]

// ─── Watchlists ───

// Frontend-only: membership chips render client-side from
// /v2/watchlists/{ns}/memberships; never sent to the scanner API as a column.
// As a FILTER it's an enum whose options are injected live by
// useScannerColumns (value = watchlist id string, label = name); the
// datasource extracts watchlists leaves into the request's watchlist_filter
// field, resolved server-side against CURRENT membership on every query.
// Own top-level category so it's immediately findable in the picker.
const WATCHLISTS_COLUMNS: ScannerColumnDef[] = [
  // maxFilterSelections caps how many watchlists one scan can reference — the
  // filter builder stops the multi-select there; the backend enforces the same
  // ceiling. Bounds the inlined ticker literals a watchlist scan generates.
  col('watchlists', 'Watchlists', 'enum', 'Watchlists', {
    maxFilterSelections: MAX_WATCHLIST_FILTER_LISTS,
  }),
]

/**
 * Column keys that exist in the frontend catalog but must NOT be forwarded to
 * the scanner API (the backend has no concept of them). Strip these from the
 * `columns` array before building the API request.
 */
export const FRONTEND_ONLY_SCANNER_COLUMNS: Set<string> = new Set(['watchlists', 'custom_rvol'])

// ─── Assemble Full Catalog ───

// Deduplicate: columns in COMMONLY_USED also appear in intraday/premarket.
// We assign each column to its primary (most specific) group.
const SEEN = new Set<string>()
function dedup(defs: ScannerColumnDef[]): ScannerColumnDef[] {
  const result: ScannerColumnDef[] = []
  for (const d of defs) {
    if (!SEEN.has(d.key)) {
      SEEN.add(d.key)
      result.push(d)
    }
  }
  return result
}

/**
 * Profile-page key under which column profiles are persisted. The scanner and
 * watchlist column pickers intentionally SHARE this single collection so a
 * column layout saved on one surface is available on the other — they use the
 * same column catalog (SCANNER_COLUMN_CATALOG), so the profiles are fully
 * interchangeable. Both pass this as `profilePage` with type "columnProfiles".
 */
export const SHARED_COLUMN_PROFILE_PAGE = 'scanner'

export const SCANNER_COLUMN_CATALOG: ScannerColumnDef[] = [
  ...dedup(COMMONLY_USED),
  ...dedup(DAY0_INTRADAY),
  ...dedup(DAY0_PREMARKET),
  ...dedup(DAY0_AFTERHOURS),
  ...dedup(generateRanges()),
  ...dedup(generateIndicators()),
  ...dedup(generatePrevDays()),
  ...dedup(DILUTION),
  ...dedup(FINANCIALS),
  ...dedup(SHARE_STRUCTURE),
  ...dedup(COMPANY),
  ...dedup(GENERAL),
  ...dedup(WATCHLISTS_COLUMNS),
]

// ─── Derived Exports ───

/** Lookup map for O(1) column access. */
export const SCANNER_COLUMN_MAP: Record<string, ScannerColumnDef> = Object.fromEntries(
  SCANNER_COLUMN_CATALOG.map((c) => [c.key, c]),
)

/** Columns grouped by category. */
export const SCANNER_COLUMNS_BY_CATEGORY: Record<string, ScannerColumnDef[]> =
  SCANNER_COLUMN_CATALOG.reduce(
    (acc, c) => {
      ;(acc[c.category] ??= []).push(c)
      return acc
    },
    {} as Record<string, ScannerColumnDef[]>,
  )

/** Default columns shown in the scanner grid. */
export const DEFAULT_SCANNER_COLUMNS = [
  'ticker',
  'total_news_count',
  'session_lp',
  'session_v',
  'session_chg_p',
  'sharesfloat',
]
